import AuthenticationStateService from "./AuthenticationState.service";
import UserPreferencesStateService from "src/userPreferences/UserPreferencesStateService";
import UserPreferencesService from "src/userPreferences/UserPreferencesService/userPreferences.service";
import { TabiyaUser, Token, TokenHeader } from "src/auth/auth.types";
import { validateLocale } from "src/i18n/constants";
import i18n from "src/i18n/i18n";
import { jwtDecode } from "jwt-decode";
import { PersistentStorageService } from "src/app/PersistentStorageService/PersistentStorageService";
import { RestAPIError } from "src/error/restAPIError/RestAPIError";
import { StatusCodes } from "http-status-codes";
import LocaleSyncService from "src/i18n/LocaleSyncService";

export const CLOCK_TOLERANCE = 10; // 10 second buffer for expiration and issuance time, in case of clock skew

export enum TokenValidationFailureCause {
  TOKEN_EXPIRED = "TOKEN_EXPIRED",
  TOKEN_NOT_A_JWT = "TOKEN_NOT_A_JWT",
  TOKEN_NOT_SIGNED = "TOKEN_NOT_SIGNED",
  TOKEN_DOES_NOT_HAVE_A_KEY_ID = "TOKEN_DOES_NOT_HAVE_A_KEY_ID",
  ERROR_DECODING_TOKEN = "ERROR_DECODING_TOKEN",
}

/**
 * Abstract class representing an authentication service.
 *
 * Its responsibilities are:
 * (a) to offer a common interface for all authentication services.
 * This class defines the interface that specific authentication service implementations should implement,
 * which includes how to refresh tokens, cleanup operations, and logout.
 *
 * (b) to manage the application state and user preferences.
 * Additionally, this class provides callback methods that should be called by the child classes when specific events occur to update the application state,
 * which includes the authentication state and user preferences. Subclasses should manage the state directly
 *
 * All instances of AuthenticationServices should be singletons.
 */
abstract class AuthenticationService {
  protected readonly authenticationStateService: AuthenticationStateService;
  protected readonly userPreferencesStateService: UserPreferencesStateService;

  protected constructor() {
    this.authenticationStateService = AuthenticationStateService.getInstance();
    this.userPreferencesStateService = UserPreferencesStateService.getInstance();
  }

  /**
   * Refreshes the authentication token.
   * This method should be called to schedule a refresh of the token
   * It should obtain a new token from the authentication provider and update it in the application state.
   * @returns {Promise<void>} A promise that resolves when the token has been refreshed.
   */
  abstract refreshToken(): Promise<void>;

  /**
   * Performs cleanup operations when the application unmounts.
   * This method should be used to clear any artifacts, listeners, or timeouts set up by the authentication service.
   */
  abstract cleanup(): void;

  /**
   * Logs out the current user.
   * This method should clear all user-related data from the application state and storage.
   * @returns {Promise<void>} A promise that resolves when the logout process is complete.
   */
  abstract logout(): Promise<void>;

  /**
   * Retrieves user information based on the provided token.
   * This method should decode the token and extract relevant user data.
   * @param {string} token - The authentication token.
   * @returns {TabiyaUser | null} The user object if the token is valid, null otherwise.
   */
  abstract getUser(token: string): TabiyaUser | null;

  /**
   * Checks if an active session, especially when using third party authentication providers.
   * like firebase or OAuth providers, exists.
   *
   * @returns {boolean} True if an active session exists, false otherwise.
   */
  abstract isProviderSessionValid(): Promise<boolean>;

  /**
   * Updates the application state when a successful logout occurs
   */
  async onSuccessfulLogout(): Promise<void> {
    // clear the user from the context, and the persistent storage
    this.authenticationStateService.clearUser();
    this.authenticationStateService.clearToken();
    // clear the userPreferences from the "state"
    this.userPreferencesStateService.clearUserPreferences();
    // clear the personal info from the persistent storage only if the user is successfully logged out
    PersistentStorageService.clearPersonalInfo();
    PersistentStorageService.clearAccountConverted();
    // Dont clear the login method, we want to preserve the login method for other logins (e.g on a different tab)
    // the login method will be overwritten by the next login (doesnt need to be cleared)
  }

  /**
   * Updates the application state when a successful login occurs
   * @throws {FirebaseError} If the user is not found after successful login
   */
  async onSuccessfulLogin(token: string): Promise<void> {
    const user = this.getUser(token);
    if (!user) {
      throw Error("User not found in the token");
    }
    this.authenticationStateService.setUser(user);
    this.authenticationStateService.setToken(token);
    let prefs = null;
    try {
      prefs = await UserPreferencesService.getInstance().getUserPreferences(user.id);
    } catch (error) {
      if (error instanceof RestAPIError) {
        // if the user preferences are not found by user id, but has a valid token, log an info and continue with the prefs as null
        if (error.statusCode === StatusCodes.NOT_FOUND) {
          console.info(`User has not registered! Preferences could not be found for userId: ${user.id}`);
          return;
        }
      }
      // rethrow the error if it is not a 404 error
      throw error;
    }
    if (prefs !== null) {
      // set the local preferences "state" (for lack of a better word)
      UserPreferencesStateService.getInstance().setUserPreferences(prefs);

      // Sync locale on login: if the frontend locale differs from the backend, update backend
      // If a user logs in, we need to update the backend with the language they are using, so that it follows.
      // It might be a bad idea to start on compass in English and realize you are having a conversation in Spanish
      const frontendLocale = validateLocale(i18n.language);
      await LocaleSyncService.getInstance().syncOnLogin(prefs.language, frontendLocale);
    }
  }

  /**
   * Updates the application state when a successful registration occurs
   * @throws {FirebaseError} If the user is not found after successful registration
   */
  async onSuccessfulRegistration(token: string, registrationCode: string | undefined): Promise<void> {
    const user = this.getUser(token);
    if (!user) {
      throw Error("User not found in the token");
    }
    this.authenticationStateService.setUser(user);
    this.authenticationStateService.setToken(token);
    // create user preferences for the first time.
    // in order to do this, there needs to be a logged-in user in the persistent storage
    // Use the current i18n locale instead of a hardcoded default
    const currentLocale = validateLocale(i18n.language);
    const prefs = await UserPreferencesService.getInstance().createUserPreferences({
      user_id: user.id,
      invitation_code: registrationCode,
      language: currentLocale,
    });
    UserPreferencesStateService.getInstance().setUserPreferences(prefs);
  }

  /**
   * Updates the application state when a successful refresh occurs
   * @param {string} token - The authentication token
   */
  async onSuccessfulRefresh(token: string): Promise<void> {
    const user = this.getUser(token);
    if (!user) {
      throw Error("User not found in the token");
    }
    this.authenticationStateService.setUser(user);
    this.authenticationStateService.setToken(token);
  }

  /**
   * Checks the header and payload of the token to ensure it is a valid JWT
   *
   * @param {string} token - The token to validate.
   * @returns {boolean} True if the token is valid, false otherwise.
   */
  public isTokenValid(token: string): { isValid: boolean; decodedToken: Token | null; failureCause?: string } {
    try {
      // Decode the header and validate it
      const decodedHeader: TokenHeader = jwtDecode(token, { header: true });
      if (decodedHeader.typ !== "JWT") {
        console.debug("Token is not a JWT");
        return { isValid: false, decodedToken: null, failureCause: TokenValidationFailureCause.TOKEN_NOT_A_JWT };
      }
      if (!decodedHeader.alg) {
        console.debug("Token is not signed");
        return { isValid: false, decodedToken: null, failureCause: TokenValidationFailureCause.TOKEN_NOT_SIGNED };
      }
      if (!decodedHeader.kid) {
        console.debug("Token does not have a key ID (kid)");
        return {
          isValid: false,
          decodedToken: null,
          failureCause: TokenValidationFailureCause.TOKEN_DOES_NOT_HAVE_A_KEY_ID,
        };
      }

      // Decode the token and validate it
      // Note: we intentionally do not check expiry or issuance time here.
      // Device clocks can be skewed (ahead or behind), which causes false negatives.
      // Instead, we rely on the server returning 401 to trigger a token refresh.
      const decodedToken: Token = jwtDecode(token);
      return { isValid: true, decodedToken: decodedToken };
    } catch (error) {
      return { isValid: false, decodedToken: null, failureCause: TokenValidationFailureCause.ERROR_DECODING_TOKEN };
    }
  }
}

export default AuthenticationService;
